import { Component, EventEmitter, Input, Output, type OnInit } from '@angular/core'
import { FormBuilder, FormControl, Validators, FormGroup, FormArray } from '@angular/forms'
import { Store } from '@ngrx/store'
import * as lodash from 'lodash'
import {
  availablePropertyClassTypes,
  bdcLanguages,
  getBdcTimeOptions,
  BdcContentV2Service,
  BdcTranslation,
  BdcFacilityState,
  BdcPropertyExtendedFacilityMetadataItem,
  BdcPropertyExtendedFacility,
  isBdcContentDisabled,
  BdcPropertyDetails,
} from '@tokeet-frontend/channel-content'
import {
  Rental,
  TimePickerUtils,
  ActionFailed,
  SaveForm,
  integer,
  ParsedGooglePlace,
  countries,
  isSomething,
  Toaster,
  compactObject,
  ConfirmDialogService,
  ConfirmDialogStatus,
} from '@tokeet-frontend/tv3-platform'
import { finalize, switchMap, tap } from 'rxjs/operators'
import { GpsLocation } from '@tv3/shared/map-control/map-control.component'
import { combineLatest, of } from 'rxjs'

@Component({
  selector: 'app-bdc-property-details',
  templateUrl: './bdc-property-details.component.html',
  styleUrls: ['./bdc-property-details.component.scss'],
})
export class BdcPropertyDetailsComponent implements OnInit {
  @Input() propertyId: number
  @Input() isLoading = false
  @Input() set property(property: BdcPropertyDetails) {
    if (property) {
      this.setForm(property)
    }
  }
  @Output() propertyChange = new EventEmitter<BdcPropertyDetails>()

  isEditDisabled: boolean | string[] = false

  availablePropertyClassTypes = availablePropertyClassTypes
  languages = bdcLanguages
  timeOptions = getBdcTimeOptions()

  rentalCtrl = new FormControl()
  gpsLocationControl = new FormControl()

  physicalAddressForm = this.fb.group({
    address_line: [null, [Validators.required]],
    city_name: [null, [Validators.required]],
    country_code: [null, [Validators.required]],
    postal_code: [],
    display_address: [],
  })

  checkInForm = this.fb.group({
    from: [null, [Validators.required]],
    until: [],
  })

  checkOutForm = this.fb.group({
    from: [],
    until: [],
  })

  form = this.fb.group({
    property_name: [null, [Validators.required]],
    property_category: [null, [Validators.required]],
    position: [null, [Validators.required]],
    check_in: this.checkInForm,
    check_out: this.checkOutForm,
    room_count: [null, [Validators.required, Validators.min(0)]],
    physical_address: this.physicalAddressForm,
    primary_language: [],
    languages_spoken: [[]],
    translations: this.fb.array([]),
    floor_count: [null, [Validators.min(0), Validators.max(200), integer()]],
    stars: [0],
    currency_code: [],
    provider_property_id: [],
  })

  get translationsFormArray() {
    return this.form.get('translations') as FormArray
  }

  tokeet2BDCAmenities: Record<string, BdcPropertyExtendedFacilityMetadataItem> = {}
  propertyAmenities: BdcPropertyExtendedFacility[] = []
  activeBdcAmenityIds: number[] = []
  missingBdcAmenityIds: number[] = []
  isApplyingTokeetRentalAmenities = false

  constructor(
    private fb: FormBuilder,
    private toaster: Toaster,
    private store: Store<any>,
    private confirm: ConfirmDialogService,
    private contentService: BdcContentV2Service
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.contentService.getPropertyAmenities(),
      this.contentService.getPropertyFacilities(this.propertyId),
    ]).subscribe(([amenities, facilities]) => {
      this.tokeet2BDCAmenities = lodash.keyBy(amenities, (t) => t.tokeet_amenity)
      this.propertyAmenities = facilities
    })

    this.gpsLocationControl.valueChanges.subscribe((location: GpsLocation) => {
      if (isSomething(location)) {
        this.form.patchValue({
          position: {
            latitude: location.lat,
            longitude: location.long,
          },
        })
      }
    })
  }

  setForm(property: BdcPropertyDetails) {
    this.form.patchValue({ ...property })
    if (isSomething(property.position)) {
      this.gpsLocationControl.setValue({
        lat: +property.position.latitude,
        long: +property.position.longitude,
      })
    }
    property.translations.forEach((translation) => {
      this.translationsFormArray.push(this.createTranslationForm(translation))
    })
    this.isEditDisabled = isBdcContentDisabled(property.status)
    this.disableForm()
  }

  disableForm() {
    if (typeof this.isEditDisabled !== 'boolean') {
      this.isEditDisabled.forEach((field) => {
        this.form.get(field)?.disable()
      })
      this.rentalCtrl.disable()
      this.gpsLocationControl.disable()
    } else if (this.isEditDisabled === true) {
      this.form.disable()
      this.rentalCtrl.disable()
      this.gpsLocationControl.disable()
    }
  }

  onRentalChange([rental]: Rental[]) {
    const address = rental.address
    const property_category = availablePropertyClassTypes.find((t) => t.name === rental.type)?.code
    this.form.patchValue({
      property_name: rental.name,
      room_count: rental.bedrooms,
      property_category: property_category || this.form.get('property_category')?.value,
      physical_address: {
        address_line: address?.address,
        city_name: address?.city,
        country_code: address?.countryCode,
        postal_code: address?.postalCode || address?.zip,
      },
      check_in: {
        from: TimePickerUtils.formatTime(rental.checkin, 'HH:mm'),
      },
      check_out: {
        from: TimePickerUtils.formatTime(rental.checkout, 'HH:mm'),
      },
      currency_code: rental.currency?.code,
      primary_language:
        rental.locale &&
        bdcLanguages.find((l) => lodash.lowerCase(l.code).startsWith(lodash.lowerCase(rental.locale)))?.code,
    })

    this.gpsLocationControl.setValue({
      lat: rental.gps?.lat,
      long: rental.gps?.long,
    })

    this.confirm
      .openRegular({
        title: $localize`Copy Amenities from Rental`,
        body: $localize`Would you like to copy the amenities from the rental to the property?`,
      })
      .subscribe((status) => {
        if (status === ConfirmDialogStatus.Confirmed) {
          this.isApplyingTokeetRentalAmenities = true
          // tokeet amenities to bdc amenities
          this.activeBdcAmenityIds = lodash.compact(
            lodash
              .toPairs(rental.detail)
              .filter(([k, v]) => v)
              .map(([k]) => k)
              .map((k) => this.tokeet2BDCAmenities[k]?.facility_id)
          )
          this.missingBdcAmenityIds = this.propertyAmenities
            .filter((facility) => !this.activeBdcAmenityIds.includes(facility.facility_id))
            .map((facility) => facility.facility_id)
        } else {
          this.isApplyingTokeetRentalAmenities = false
        }
      })
  }

  onPlaceChange(parsedPlace: ParsedGooglePlace) {
    const country = countries.find((c) => c.id === parsedPlace.countryShort)
    this.physicalAddressForm.patchValue({
      address_line: (parsedPlace.streetNumber ? parsedPlace.streetNumber + ' ' : '') + (parsedPlace.street || ''),
      city_name: parsedPlace.city,
      country_code: country?.id,
      postal_code: parsedPlace.postCode,
    })

    this.gpsLocationControl.setValue({
      lat: parsedPlace.gps.lat,
      long: parsedPlace.gps.long,
    })
  }

  createTranslationForm(translation?: BdcTranslation) {
    const form = this.fb.group({
      city_name: [translation?.city_name, [Validators.required]],
      address_line: [translation?.address_line, [Validators.required]],
      language_code: [translation?.language_code, [Validators.required]],
      property_name: [translation?.property_name, [Validators.required]],
    })
    return form
  }

  onAddTranslation() {
    this.translationsFormArray.push(this.createTranslationForm())
  }

  onDeleteTranslation(index: number) {
    this.translationsFormArray.removeAt(index)
  }

  saveAmenities(propertyId: number) {
    if (!this.isApplyingTokeetRentalAmenities) {
      return of(null)
    }
    const data = this.activeBdcAmenityIds?.map((id) => ({
      facility_id: id,
      state: BdcFacilityState.PRESENT,
    }))
    const missingData = this.missingBdcAmenityIds?.map((id) => ({
      facility_id: id,
      state: BdcFacilityState.MISSING,
    }))
    return this.contentService.updatePropertyFacilities(propertyId, [...data, ...missingData])
  }

  @SaveForm()
  onSave(form: FormGroup) {
    this.isLoading = true
    let payload = compactObject(form.value)
    if (typeof this.isEditDisabled !== 'boolean') {
      payload = lodash.omit(payload, this.isEditDisabled)
    }
    this.contentService
      .updateProperty(this.propertyId, compactObject(payload))
      .pipe(
        tap((data) => this.propertyChange.emit(data)),
        finalize(() => (this.isLoading = false)),
        switchMap(() => this.saveAmenities(this.propertyId))
      )
      .subscribe(
        () => {
          this.toaster.success($localize`Property details updated successfully`)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }
}
